import {readyAuth} from './readyAuth';
import { database } from '../firebase';
import { ref, get, child } from 'firebase/database';

const CACHE_DURATION = 60 * 60 * 1000; // 1시간 캐시

/**
 * Firebase Realtime Database에서 최신 ETF 데이터를 가져오는 함수
 * @returns {Promise<Object>} ETF 데이터 객체
 */
export const getLatestETFData = async () => {
  try {
    // 캐시 확인
    const cachedData = localStorage.getItem('etf_latest_data');
    const cachedTimestamp = localStorage.getItem('etf_latest_timestamp');

    if (cachedData && cachedTimestamp) {
      const now = new Date().getTime();
      if (now - parseInt(cachedTimestamp) < CACHE_DURATION) {
        console.log('캐시된 ETF 데이터를 사용합니다.');
        return JSON.parse(cachedData);
      }
    }

    await readyAuth();
    const dbRef = ref(database);
    const snapshot = await get(child(dbRef, 'etf/latest'));
    
    if (snapshot.exists()) {
      const data = snapshot.val();
      console.log('Firebase에서 ETF 데이터를 성공적으로 가져왔습니다:', {
        date: data.collection_info?.date,
        totalETFs: data.collection_info?.total_etfs,
        timestamp: data.collection_info?.timestamp
      });

      // 데이터 캐싱
      try {
        localStorage.setItem('etf_latest_data', JSON.stringify(data));
        localStorage.setItem('etf_latest_timestamp', new Date().getTime().toString());
      } catch (e) {
        console.warn('ETF 데이터 캐싱 실패 (용량 초과 가능성):', e);
      }

      return data;
    } else {
      console.log('Firebase에서 데이터를 찾을 수 없습니다.');
      return null;
    }
  } catch (error) {
    console.error('Firebase에서 데이터를 가져오는 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 특정 날짜의 일일 스냅샷 데이터를 가져오는 함수
 * @param {string} date - 날짜 (YYYY_MM_DD 형식)
 * @returns {Promise<Object>} 일일 스냅샷 데이터
 */
export const getDailySnapshot = async (date) => {
  try {
    await readyAuth();
    const dbRef = ref(database);
    const snapshot = await get(child(dbRef, `etf/daily_snapshots/${date}`));
    
    if (snapshot.exists()) {
      return snapshot.val();
    } else {
      console.log(`${date} 날짜의 스냅샷 데이터를 찾을 수 없습니다.`);
      return null;
    }
  } catch (error) {
    console.error('일일 스냅샷 데이터를 가져오는 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 마스터 데이터를 가져오는 함수
 * @returns {Promise<Object>} 마스터 데이터
 */
export const getMasterData = async () => {
  try {
    await readyAuth();
    const dbRef = ref(database);
    const snapshot = await get(child(dbRef, 'etf/master_data'));
    
    if (snapshot.exists()) {
      return snapshot.val();
    } else {
      console.log('마스터 데이터를 찾을 수 없습니다.');
      return null;
    }
  } catch (error) {
    console.error('마스터 데이터를 가져오는 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 여러 날짜의 데이터를 한 번에 가져오는 함수
 * @param {Array<string>} dates - 날짜 배열 (YYYY_MM_DD 형식)
 * @returns {Promise<Object>} 날짜별 데이터 객체
 */
export const getMultipleDailySnapshots = async (dates) => {
  try {
    const promises = dates.map(date => getDailySnapshot(date));
    const results = await Promise.all(promises);
    
    const dataByDate = {};
    dates.forEach((date, index) => {
      if (results[index]) {
        dataByDate[date] = results[index];
      }
    });
    
    return dataByDate;
  } catch (error) {
    console.error('여러 날짜 데이터를 가져오는 중 오류 발생:', error);
    throw error;
  }
};
